import { OffSymbol, OnSymbol } from "components/Common/SwitchSymbols"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"
import { Col, Row } from "react-bootstrap"
import Select, { SingleValue } from "react-select"
import Switch from "react-switch"
import { VesselDetails } from "../hooks/usePrevetAPI"
import { VesselSegmentInputSelection } from "./PrevetMain"

interface PrevetModalInfoProps {
  vesselDetails: VesselDetails
  timeWindow: { label: string; value: number }
  setTimeWindow: (option: { label: string; value: number }) => void
  showTradingPatternMarkers: boolean
  setShowTradingPatternMarkers: (isShown: boolean) => void
  showPortStayMarkers: boolean
  setShowPortStayMarkers: (isShown: boolean) => void
  chosenSegment: VesselSegmentInputSelection
}

export interface TimeWindow {
  label: string
  value: number
}

// TODO: Name is old prop from researchAPI, this is done for
// backward compatibility between staging and prod as the
// researchAPI is more updated on staging (13 Apr 2023)

export function PrevetModalInfo({
  timeWindow,
  setTimeWindow,
  showTradingPatternMarkers,
  setShowTradingPatternMarkers,
  showPortStayMarkers,
  setShowPortStayMarkers,
  chosenSegment,
}: PrevetModalInfoProps) {
  const { postTrackingEvent } = useWoopraAPI()

  return (
    <>
      <Row className="mt-1">
        <Col md={6} sm={6} xs={6}>
          <h5 className="font-size-14 mt-2 mb-3">Trading Markers</h5>
          <Switch
            uncheckedIcon={<OffSymbol />}
            checkedIcon={<OnSymbol />}
            onColor="#3050A2"
            onChange={(trading_pattern) => {
              setShowTradingPatternMarkers(trading_pattern)
              postTrackingEvent({
                page: "/prevetting",
                event: "switch_trading_markers",
                properties: {
                  segment: chosenSegment.value,
                  component: "trade map",
                },
              })
            }}
            checked={showTradingPatternMarkers}
          />
        </Col>
        <Col md={6} sm={6} xs={6}>
          <h5 className="font-size-14 mt-2 mb-3">Port Stay Markers</h5>
          <Switch
            uncheckedIcon={<OffSymbol />}
            checkedIcon={<OnSymbol />}
            onColor="#3050A2"
            onChange={(port_stay) => {
              setShowPortStayMarkers(port_stay)
              postTrackingEvent({
                page: "/prevetting",
                event: "switch_portstay_markers",
                properties: {
                  segment: chosenSegment.value,
                  component: "trade map",
                },
              })
            }}
            checked={showPortStayMarkers}
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <h5 className="font-size-14 mt-3 mb-3">
            Trading Pattern Time Window
          </h5>
          <Select
            value={timeWindow}
            onChange={(timeWindow: SingleValue<TimeWindow>) => {
              if (!timeWindow) {
                return
              }
              setTimeWindow(timeWindow)
              postTrackingEvent({
                page: "/prevetting",
                event: "trading_pattern_time_window",
                properties: {
                  segment: chosenSegment.value,
                  component: "trade map",
                },
              })
            }}
            options={[
              {
                options: [
                  { label: "1 Month", value: 1 },
                  { label: "3 Months", value: 3 },
                  { label: "6 Months", value: 6 },
                  { label: "1 year", value: 12 },
                  { label: "2 years", value: 24 },
                ],
              },
            ]}
            classNamePrefix="select2-selection"
          />
        </Col>
      </Row>
      <hr />

      {/* Speed Legend */}
      <h5 className="font-size-14 mt-3 mb-3">Speed</h5>
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-0 align-items-baseline">
          <div className="dash" style={{ backgroundColor: getSpeedColor(12) }}>
            <span></span>
          </div>
          <p>12kt+</p>
        </div>
        <div className="d-flex gap-0 align-items-baseline">
          <div className="dash" style={{ backgroundColor: getSpeedColor(10) }}>
            <span></span>
          </div>
          <p>9-11.9kt</p>
        </div>
        <div className="d-flex gap-0 align-items-baseline">
          <div className="dash" style={{ backgroundColor: getSpeedColor(8) }}>
            <span></span>
          </div>
          <p>8.9kt below</p>
        </div>
      </div>

      {/* Port stay legend */}
      <h5 className="font-size-14 mt-3 mb-3">Port Stay</h5>
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-0">
          <div
            className="circle"
            style={{ backgroundColor: getSpeedColor(12) }}
          >
            <span></span>
          </div>
          <p>0-9 days</p>
        </div>
        <div className="d-flex gap-0">
          <div
            className="circle"
            style={{ backgroundColor: getSpeedColor(10) }}
          >
            <span></span>
          </div>
          <p>10-19 days</p>
        </div>
        <div className="d-flex gap-0">
          <div className="circle" style={{ backgroundColor: getSpeedColor(8) }}>
            <span></span>
          </div>
          <p>20+ days</p>
        </div>
      </div>
    </>
  )
}

// Function to get color based on speed
const getSpeedColor = (speed: number): string => {
  if (speed >= 12) return "#02a499" // Green for 12kt+
  if (speed >= 9 && speed < 12) return "#ffdd15" // Yellow for 9-11.9kt
  return "#ec4561" // Red for 0-8.9kt
}
